/**
 * useChecklistOperations Hook
 * Extracted from ChecklistComponent.tsx
 * Handles all checklist operations: toggle, update, delete, add, reorder
 */

import { useState, useCallback } from 'react';
import { ChecklistItem } from '@/shared/types/projectData';
import { arrayMove } from '@dnd-kit/sortable';
import { useModalEscape } from '@/shared/services/KeyboardEventManager';

interface UseChecklistOperationsProps {
  checklist: ChecklistItem[];
  onChange: (updatedChecklist: ChecklistItem[]) => void;
}

export function useChecklistOperations({
  checklist,
  onChange,
}: UseChecklistOperationsProps) {
  const [newItemText, setNewItemText] = useState('');
  const [isAddingItem, setIsAddingItem] = useState(false);

  const closeComposer = useCallback(() => {
    setNewItemText('');
    setIsAddingItem(false);
  }, []);

  // The keyboard manager handles Escape in the capture phase and stops it
  // before the composer's own key handler runs. While the composer is open it
  // claims Escape above the hosting panel so the first press closes only the
  // composer and the next press reaches the panel.
  useModalEscape('checklist-composer', closeComposer, {
    priority: 400,
    enabled: isAddingItem,
  });

  const handleDragEnd = useCallback((event: { active: { id: string | number }; over: { id: string | number } | null }) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = checklist.findIndex(item => item.id === active.id);
    const newIndex = checklist.findIndex(item => item.id === over.id);
    if (oldIndex < 0 || newIndex < 0) {
      return;
    }

    const reorderedItems = arrayMove(checklist, oldIndex, newIndex).map((item, index) => ({
      ...item,
      order: index,
      updatedAt: new Date().toISOString()
    }));

    onChange(reorderedItems);
  }, [checklist, onChange]);

  const handleToggleItem = useCallback((id: string) => {
    const updatedItems = checklist.map(item =>
      item.id === id
        ? {
            ...item,
            checked: !item.checked,
            updatedAt: new Date().toISOString()
          }
        : item
    );
    onChange(updatedItems);
  }, [checklist, onChange]);

  const handleUpdateItem = useCallback((id: string, text: string) => {
    const updatedItems = checklist.map(item =>
      item.id === id
        ? {
            ...item,
            text,
            updatedAt: new Date().toISOString()
          }
        : item
    );
    onChange(updatedItems);
  }, [checklist, onChange]);

  const handleDeleteItem = useCallback((id: string) => {
    // Simple approach: First uncheck the item, then delete it in one operation
    const updatedItems = checklist.map(item =>
      item.id === id
        ? { ...item, checked: false, updatedAt: new Date().toISOString() }
        : item
    ).filter(item => item.id !== id);

    onChange(updatedItems);
  }, [checklist, onChange]);

  const handleAddItem = useCallback(() => {
    if (newItemText.trim()) {
      const newItem: ChecklistItem = {
        id: `checklist-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        text: newItemText.trim(),
        checked: false,
        order: checklist.length,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      onChange([...checklist, newItem]);
      setNewItemText('');
      // Keep the composer mounted so Enter behaves like a natural continuous
      // checklist entry flow.  Blur and Escape remain the explicit close paths.
      setIsAddingItem(true);
    }
  }, [newItemText, checklist, onChange]);

  const handleAddItemKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddItem();
    } else if (e.key === 'Escape') {
      closeComposer();
    }
  }, [closeComposer, handleAddItem]);

  return {
    // State
    newItemText,
    setNewItemText,
    isAddingItem,
    setIsAddingItem,
    // Handlers
    handleDragEnd,
    handleToggleItem,
    handleUpdateItem,
    handleDeleteItem,
    handleAddItem,
    handleAddItemKeyDown,
  };
}
